// TakeCard — one draft take in the Recording Booth's takes column (split out
// of RecordingStudio.tsx). Waveform preview, play/pause with sibling
// mutual-exclusion, mark ("Add to project") and delete.

import { useCallback, useEffect, useRef, useState } from 'react';
import { getStsParamSummary } from '@/lib/paramSummary';
import { Waveform } from '@/components/Waveform/Waveform';
import type { DraftTake } from './boothTypes';
import styles from './RecordingStudio.module.css';

// ── TakeCard ─────────────────────────────────────────────────────────────
export interface TakeCardProps {
  label: string;
  take: DraftTake;
  playing: boolean;
  shouldPause: boolean;
  /** Multi-voice indicator (#173 Phase 6). When present, renders a colored
   *  dot + voice name in the take header so the talent can tell two
   *  same-session takes apart at a glance. Omitted (null) for single-voice
   *  assets — the existing "Take {s}-{n}" label is sufficient. */
  voiceIndicator: { color: string; label: string } | null;
  onPlayStart(id: string): void;
  onPlayStop(): void;
  onToggleMark(id: string): void;
  onDelete(id: string): void;
}

export function TakeCard({
  label,
  take,
  playing,
  shouldPause,
  voiceIndicator,
  onPlayStart,
  onPlayStop,
  onToggleMark,
  onDelete,
}: TakeCardProps) {
  const wsRef = useRef<import('@/components/Waveform/Waveform').WaveformHandle | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Tracks whether the Waveform's audio buffer has finished decoding +
  // loading. Auto-play has to wait for this — Waveform.play() silently
  // no-ops against a null buffer, which was the source of the
  // unreliable autoplay after Generate (#173 follow-up).
  const [isReady, setIsReady] = useState(false);

  // Reset readiness whenever the take's audio reference changes (overwrite
  // path: ReGenerate replaces the take's audioBuffer; Waveform re-decodes).
  useEffect(() => {
    setIsReady(false);
  }, [take.audioBuffer]);

  // Pause when a sibling card starts playing.
  useEffect(() => {
    if (shouldPause && isPlaying) {
      wsRef.current?.pause();
      setIsPlaying(false);
    }
  }, [shouldPause, isPlaying]);

  // Pause when the parent says we're no longer the active player.
  useEffect(() => {
    if (!playing && isPlaying) {
      wsRef.current?.pause();
      setIsPlaying(false);
    }
  }, [playing, isPlaying]);

  // Auto-play once both signals line up: parent says this card should play
  // AND the Waveform has its audio buffer decoded. Without the ready gate,
  // Waveform.play() called against a null bufferRef silently no-ops and the
  // play state desyncs (icon shows pause, no audio).
  useEffect(() => {
    if (playing && isReady && !isPlaying) {
      wsRef.current?.seekTo(0);
      wsRef.current?.play();
      setIsPlaying(true);
      // Scroll the card into view if it's off-screen in the takes-list
      // scroller. `block: 'nearest'` is a no-op when already visible —
      // exactly the "only if not visible" semantic we want.
      cardRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [playing, isReady, isPlaying]);

  const handleWsReady = useCallback(() => {
    setIsReady(true);
  }, []);

  // Stable callbacks via refs so the Waveform's WaveSurfer instance doesn't
  // tear down + rebuild on every parent render (the cause of the flicker
  // during preroll / countdown).
  const takeId = take.id;
  const handleWsPlay = useCallback(() => {
    onPlayStart(takeId);
    setIsPlaying(true);
  }, [onPlayStart, takeId]);
  const handleWsPause = useCallback(() => {
    setIsPlaying(false);
  }, []);
  const handleWsFinish = useCallback(() => {
    setIsPlaying(false);
    onPlayStop();
  }, [onPlayStop]);

  const handlePlayToggle = () => {
    const ws = wsRef.current;
    if (!ws) return;
    if (ws.isPlaying()) {
      ws.pause();
      setIsPlaying(false);
    } else {
      onPlayStart(takeId);
      // Rewind to start if we just finished — otherwise WS resumes from
      // its current cursor (which may be at the end after a 'finish').
      ws.seekTo(0);
      ws.play();
      setIsPlaying(true);
    }
  };

  return (
    <div
      ref={cardRef}
      className={`${styles.takeCard} ${take.marked ? styles.takeCardMarked : ''}`}
    >
      <div className={styles.takeHeader}>
        <span className={styles.takeLabel}>
          {voiceIndicator && (
            <span
              className={styles.takeVoiceDot}
              style={{ background: voiceIndicator.color }}
              title={voiceIndicator.label}
              aria-label={voiceIndicator.label}
            />
          )}
          <span>{label}</span>
          {voiceIndicator && (
            <span className={styles.takeVoiceLbl}>{voiceIndicator.label}</span>
          )}
        </span>
        <div className={styles.takeHeaderRight}>
          <span className={styles.takeDuration}>{fmtDur(take.duration)}</span>
          <button
            type="button"
            className={styles.takeDeleteBtn}
            onClick={() => onDelete(takeId)}
            title="Delete take"
            aria-label="Delete take"
          >
            <i className="ph-bold ph-x" />
          </button>
        </div>
      </div>
      <div className={styles.takeBody}>
        <button
          type="button"
          className={styles.takePlayBtn}
          onClick={handlePlayToggle}
          title={isPlaying ? 'Pause' : 'Play'}
          aria-label={isPlaying ? 'Pause take' : 'Play take'}
        >
          <i className={isPlaying ? 'ph-fill ph-pause' : 'ph-fill ph-play'} />
        </button>
        <div className={styles.takeWave}>
          <Waveform
            ref={wsRef}
            audio={take.audioBuffer}
            height={48}
            onPlay={handleWsPlay}
            onPause={handleWsPause}
            onFinish={handleWsFinish}
            onReady={handleWsReady}
          />
        </div>
      </div>
      <div className={styles.takeParamLine} title="Generation params used for this take">
        {getStsParamSummary(take.params)}
      </div>
      <div className={styles.takeFooter}>
        <button
          type="button"
          className={`${styles.takeMarkBtn} ${take.marked ? styles.takeMarkBtnOn : ''}`}
          onClick={() => onToggleMark(takeId)}
          aria-pressed={take.marked}
        >
          <i
            className={
              take.marked
                ? 'ph-fill ph-check-circle'
                : 'ph-bold ph-check-circle'
            }
          />
          <span>{take.marked ? 'In project' : 'Add to project'}</span>
        </button>
      </div>
    </div>
  );
}

function fmtDur(sec: number): string {
  if (!Number.isFinite(sec) || sec <= 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec - m * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
